import React, { useEffect, useMemo, useState } from 'react';

import clsxm from '@/lib/clsxm';
import {
  TextPresetOption,
  textPresetOptions,
} from '@/lib/workflows/textPresets';

import Modal from '@/components/atoms/Modal';

import { PresetCategory, visiblePresetCategories } from '@/constants/presets';

interface TextPresetSelectorModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (preset: TextPresetOption) => void;
}

export function TextPresetSelectorModal({
  open,
  onClose,
  onSelect,
}: TextPresetSelectorModalProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<
    PresetCategory | 'All'
  >('All');

  useEffect(() => {
    if (!open) {
      setSearchQuery('');
      setSelectedCategory('All');
    }
  }, [open]);

  const filteredPresets = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return textPresetOptions.filter((preset) => {
      const categories = Array.isArray(preset.preset.category)
        ? preset.preset.category
        : [preset.preset.category];

      if (
        selectedCategory !== 'All' &&
        !categories.includes(selectedCategory)
      ) {
        return false;
      }

      if (!query) return true;

      const keywords = (preset.preset.searchKeywords || [])
        .join(' ')
        .toLowerCase();
      return (
        preset.label.toLowerCase().includes(query) ||
        preset.description.toLowerCase().includes(query) ||
        preset.value.toLowerCase().includes(query) ||
        keywords.includes(query)
      );
    });
  }, [searchQuery, selectedCategory]);

  return (
    <Modal showModal={open} onClose={onClose} className='sm:max-w-5xl'>
      <div className='p-6 space-y-4'>
        <div className='flex items-start justify-between gap-3'>
          <div>
            <h3 className='text-lg font-semibold text-gray-900 dark:text-white'>
              Choose a text preset
            </h3>
            <p className='text-sm text-gray-500 dark:text-gray-400'>
              Pick a preset before adding the node so its title matches the tool
              you need.
            </p>
          </div>
          {/* <span className='text-xs font-medium px-3 py-1 rounded-full bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-200'>
            {filteredPresets.length} presets
          </span> */}
        </div>

        <div className='flex items-center gap-3'>
          <div className='relative flex-1'>
            <input
              type='text'
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder='Search by name, keyword, or use-case'
              className='w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-700 dark:bg-slate-800 dark:text-white'
            />
            <svg
              className='pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-gray-400'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'
              viewBox='0 0 24 24'
            >
              <path d='M11 4a7 7 0 100 14 7 7 0 000-14z' />
              <path d='M21 21l-4.35-4.35' />
            </svg>
          </div>
          {searchQuery && (
            <button
              className='text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
              onClick={() => setSearchQuery('')}
            >
              Clear
            </button>
          )}
        </div>

        <div className='flex flex-wrap gap-2'>
          <button
            onClick={() => setSelectedCategory('All')}
            className={clsxm(
              'rounded-full px-3 py-1 text-xs font-medium border transition-colors',
              selectedCategory === 'All'
                ? 'bg-primary-100 text-primary-800 border-primary-200 dark:bg-primary-900/30 dark:text-primary-100 dark:border-primary-700'
                : 'bg-gray-100 text-gray-700 border-gray-200 hover:border-primary-300 hover:text-primary-700 dark:bg-slate-800 dark:text-gray-200 dark:border-gray-700 dark:hover:border-primary-600',
            )}
          >
            All
          </button>
          {visiblePresetCategories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={clsxm(
                'rounded-full px-3 py-1 text-xs font-medium border transition-colors',
                selectedCategory === category
                  ? 'bg-primary-100 text-primary-800 border-primary-200 dark:bg-primary-900/30 dark:text-primary-100 dark:border-primary-700'
                  : 'bg-gray-100 text-gray-700 border-gray-200 hover:border-primary-300 hover:text-primary-700 dark:bg-slate-800 dark:text-gray-200 dark:border-gray-700 dark:hover:border-primary-600',
              )}
            >
              {category}
            </button>
          ))}
        </div>

        <div className='grid grid-cols-1 gap-3 pt-2 sm:grid-cols-2 lg:grid-cols-3 max-h-[65vh] overflow-y-auto pr-1'>
          {filteredPresets.map((preset) => {
            const Icon = preset.preset.icon as React.ComponentType<{
              className?: string;
            }>;

            return (
              <button
                key={preset.value}
                onClick={() => onSelect(preset)}
                className={clsxm(
                  'group relative flex h-full flex-col gap-2 rounded-xl border border-gray-200 bg-gradient-to-br from-white via-white to-gray-50 p-4 text-left transition-all hover:-translate-y-0.5 hover:border-primary-400 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-700 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800',
                )}
              >
                <div className='flex items-start gap-3'>
                  <span
                    className={clsxm(
                      preset.preset.iconBackground,
                      preset.preset.iconForeground,
                      'flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg ring-4 ring-white shadow-sm dark:ring-1 dark:ring-white/5',
                    )}
                  >
                    <Icon className='h-5 w-5' />
                  </span>
                  <div className='flex-1 space-y-1'>
                    <div className='flex items-start justify-between gap-2'>
                      <h4 className='text-sm font-semibold text-gray-900 dark:text-white'>
                        {preset.label}
                      </h4>
                    </div>
                    <p className='text-xs leading-relaxed text-gray-500 line-clamp-3 dark:text-gray-400'>
                      {preset.description}
                    </p>
                  </div>
                </div>
              </button>
            );
          })}

          {filteredPresets.length === 0 && (
            <div className='col-span-full rounded-lg border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400'>
              No presets match that search. Try a different keyword.
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
